import {
  renderProtocolCopy,
  type ProtocolCopyContext,
} from "../injection-protocol/brand";
import type { ProtocolLocale, ProtocolStep } from "../injection-protocol/types";
import { splitStepCopy } from "./stepCopy";

/**
 * Turning a step's written words into words a speech engine says correctly.
 *
 * The protocol copy is written to be *read*: it separates clauses with a dash,
 * shouts the word that matters in capitals, and writes ranges and units the way
 * a label does. Handed to a text-to-speech engine unchanged, that becomes
 * "Inject dash pinch the skin", "e-x-a-c-t", "ninety hyphen ten seconds" and
 * "two five zero m c g" — and the one step where it matters most is the one
 * holding a needle.
 *
 * So the text is normalised first, here, in a pure function with tests. Three
 * rules govern what this is allowed to do:
 *
 * - **It may change how a word sounds. It may never change what it says.** No
 *   summarising, no reordering, no dropping a clause because it is long. The
 *   spoken step and the written step are the same instruction, and a patient
 *   who hears one and reads the other must not find a difference.
 * - **Units are expanded rather than lowercased.** "IU" read as a word is wrong
 *   and read letter-by-letter is worse, so it becomes "international units".
 *   The list is short and every entry is unambiguous — a unit this file is not
 *   sure about is left exactly as written.
 * - **Nothing is invented.** No added emphasis, no "take your time", no
 *   reassurance. The voice says the step, and the step was clinically reviewed.
 */

/**
 * Dose and volume units, expanded because neither pronunciation works.
 *
 * Kept deliberately short. These are the units the approved protocols actually
 * carry; anything else stays as written, because a mis-expanded unit on a dose
 * is a worse outcome than an awkwardly pronounced one.
 *
 * `G` is the needle gauge, and it is here because the vial protocols introduced
 * it: "an 18G drawing needle", "a 22G, 1.5-inch needle". Read as a letter it is
 * "eighteen gee", which is not a size anyone recognises. It is safe in this
 * catalogue and only in this catalogue — no approved protocol doses in grams,
 * and the expansion is case-sensitive, so `mg` and `µg` still match their own
 * entries first. A protocol that ever writes a dose in grams has to revisit
 * this line.
 */
export const UNIT_WORDS: Record<ProtocolLocale, Record<string, string>> = {
  en: {
    mcg: "micrograms",
    µg: "micrograms",
    ug: "micrograms",
    mg: "milligrams",
    ml: "millilitres",
    mm: "millimetres",
    G: "gauge",
    IU: "international units",
    UI: "international units",
  },
  fr: {
    mcg: "microgrammes",
    µg: "microgrammes",
    ug: "microgrammes",
    mg: "milligrammes",
    ml: "millilitres",
    mm: "millimètres",
    G: "gauge",
    IU: "unités internationales",
    UI: "unités internationales",
  },
};

const DEGREES: Record<ProtocolLocale, string> = {
  en: " degrees",
  fr: " degrés",
};

/** The singular, for "at a 90° angle" — see `speakableStepText`. */
const DEGREE: Record<ProtocolLocale, string> = {
  en: " degree",
  fr: " degré",
};

/**
 * A temperature, where the degree sign carries a scale letter.
 *
 * Read before the bare degree sign, or "below 25°C" is spoken as "25 degreesC"
 * — one invented word, at the step that tells her how to keep a vial she will
 * open again for the next twenty-eight evenings. Angles were the only degrees
 * in the catalogue until a medication arrived with storage instructions.
 */
const CELSIUS: Record<ProtocolLocale, string> = {
  en: " degrees Celsius",
  fr: " degrés Celsius",
};

/** The word joining the two ends of a range: "5-10 seconds". */
const RANGE_JOINER: Record<ProtocolLocale, string> = { en: " to ", fr: " à " };

/**
 * International units, which arrive without a number in front of them.
 *
 * `expandUnits` only fires on a unit that follows a digit, because "5 mg" is a
 * dose and "mg" alone in prose is usually part of a word. Follitropin's vial
 * step says "the correct dose in International Units (IU FSH)" — the unit named
 * rather than measured — so `IU` survived to `softenShouting`, which lower-cases
 * capital runs and read it "iu fsh", twice, on the step where she is drawing a
 * dose titrated for her.
 */
const BARE_UNIT_NAMES: Record<ProtocolLocale, [RegExp, string][]> = {
  // Only words the step already says, so the "no word is invented" guard holds:
  // both copies write "International Units" in full a clause earlier.
  en: [[/\bIU\s+FSH\b/gu, "International Units FSH"]],
  fr: [[/\bUI\s+FSH\b/gu, "Unités Internationales FSH"]],
};

/**
 * Words the copy shouts, which an all-caps token can make an engine spell.
 *
 * From two characters up. The two-letter unit symbols above survive it because
 * `expandUnits` runs first and has already turned them into words — that
 * ordering is the guarantee, not the threshold, and moving either call breaks
 * it.
 */
function softenShouting(text: string): string {
  // Two letters up, not three. French shouts "NE PAS agiter" and the `NE` was
  // left in capitals, which most engines spell out — on the one instruction in
  // the catalogue whose failure is invisible, since a shaken peptide injects
  // perfectly and does nothing. Safe at two because `expandUnits` runs first,
  // so `IU` and `UI` are already words by the time this sees the text; moving
  // either call breaks that.
  //
  // Matched with `\p{Lu}` and letter lookarounds rather than `[A-ZÀ-Ÿ]` and
  // `\b`: that range also contains the *lowercase* accented letters, and `\b`
  // treats them as non-word, so "Désinfectez" matched on its first two
  // characters and came out "désinfectez". A capital is a capital; the guard is
  // that nothing on either side is a letter.
  return text.replaceAll(/(?<!\p{L})\p{Lu}{2,}(?!\p{L})/gu, (word) =>
    word.toLowerCase(),
  );
}

/**
 * The needle gauge, and only where the number could be one.
 *
 * `G` is the one entry in the table that is ambiguous in prose — it is also the
 * symbol for grams — so it is matched on the size rather than on the letter.
 * Needles are made from 14 to 31 gauge; nothing outside that is a needle, and
 * "a 4G phone" or a dose written "1 G" is left exactly as written. The dose
 * token is the one string here that comes from her own record rather than from
 * approved copy, which is the reason this is narrow.
 */
function expandGauge(text: string, locale: ProtocolLocale): string {
  const gauge = UNIT_WORDS[locale].G;
  if (!gauge) return text;
  return text.replaceAll(
    /\b(1[4-9]|2\d|3[01])\s*G\b/gu,
    (_match, size: string) => `${size} ${gauge}`,
  );
}

function expandUnits(text: string, locale: ProtocolLocale): string {
  const units = UNIT_WORDS[locale];
  text = expandGauge(text, locale);
  // Only after a number. A bare "mg" in prose is not a dose, and this is not
  // the place to guess at one.
  return text.replaceAll(
    /(\d)\s*(µg|mcg|ug|mg|ml|mm|IU|UI)\b/gu,
    (match, digit: string, unit: string) => {
      const spoken = units[unit];
      return spoken ? `${digit} ${spoken}` : match;
    },
  );
}

/**
 * The step's words, arranged so an engine says them the way they read.
 *
 * Order matters, in two places, and both are easy to undo by accident.
 *
 * Numeric ranges are joined before the clause dash becomes a full stop. Not for
 * "34-36", which the clause rule never touches — it requires whitespace on both
 * sides — but for a range written with spaces, "5 - 10 seconds", which would
 * otherwise be read as "5. 10 seconds": two numbers, one of them sounding like
 * the end of a sentence, on a step about how long to push.
 *
 * And units are expanded before capitals are softened, or "IU" would already
 * have become "iu" and matched nothing.
 */
export function speakableStepText(
  text: string,
  locale: ProtocolLocale,
): string {
  let spoken = text;

  // "5-10 seconds" → "5 to 10 seconds". Both sides must be digits, so a
  // hyphenated word is left alone.
  spoken = spoken.replaceAll(
    /(\d)\s*[-–—]\s*(\d)/gu,
    (_match, from: string, to: string) => `${from}${RANGE_JOINER[locale]}${to}`,
  );

  // The dash the copy uses as a separator — "Inject - Pinch the skin" — becomes
  // the punctuation it was standing in for, which is what makes the engine
  // pause rather than run the two clauses together or say the word "dash".
  //
  // Which punctuation depends on what follows. A capital starts a new sentence
  // and takes a full stop; a lower-case word is an aside on the clause before
  // it and takes a comma. Everything was a full stop until the voice stopped
  // reading step headings: the dash it used to meet first was the heading's,
  // and the ones left are inside the instruction — "jusqu'à dissolution
  // complète - sans agiter" was being read as "complète. sans agiter", a full
  // stop followed by a fragment, which is heard as the engine losing its place.
  spoken = spoken.replaceAll(/\s+[-–—]\s+(\p{L})/gu, (_match, next: string) =>
    next === next.toLocaleUpperCase() && next !== next.toLocaleLowerCase()
      ? `. ${next}`
      : `, ${next}`,
  );
  // A dash with no word after it — the end of the text, or a number — keeps the
  // sentence break it always had.
  spoken = spoken.replaceAll(/\s+[-–—]\s+/gu, ". ");

  // "at a 90° angle" reads correctly and "at a 90 degrees angle" does not, so a
  // degree sign followed by the word takes the singular.
  spoken = spoken.replaceAll(
    /°(\s+(?:to|-)\s+\d+)°(\s+(?:angle|angles))/giu,
    (_match, middle: string, rest: string) =>
      `${DEGREES[locale]}${middle}${DEGREES[locale]}${rest}`,
  );
  spoken = spoken.replaceAll(
    /°(\s+(?:angle|angles))/giu,
    (_match, rest: string) => `${DEGREE[locale]}${rest}`,
  );
  // Temperatures before angles: the scale letter is part of the symbol, and
  // leaving it behind is what produced "degreesC".
  spoken = spoken.replaceAll(/°\s*C\b/gu, CELSIUS[locale]);
  spoken = spoken.replaceAll("°", DEGREES[locale]);
  spoken = expandUnits(spoken, locale);
  for (const [pattern, said] of BARE_UNIT_NAMES[locale]) {
    spoken = spoken.replaceAll(pattern, said);
  }
  spoken = softenShouting(spoken);

  // A bracket that closes onto punctuation gives the punctuation up, before the
  // general rule turns it into a comma. Without this, "(27G). Clean the vial
  // stopper" became "…27 gauge, Clean the vial stopper" — the closing comma
  // swallowed the full stop, and two instructions ran together as one. It hit
  // five steps across three medications, and in two of them the sentence it
  // glued on was a new thing to do.
  spoken = spoken.replaceAll(/\s*\)(\s*[.,;:!?])/gu, "$1");

  // The dose token renders as a parenthesised aside. Spoken, the brackets are
  // silent anyway and the comma is the pause they stand for.
  spoken = spoken.replaceAll(/\s*\(\s*/gu, ", ").replaceAll(/\s*\)\s*/gu, ", ");

  // Tidy what the substitutions left behind, without touching the words.
  spoken = spoken
    .replaceAll(/\s+/gu, " ")
    .replaceAll(/\s+([.,;:!?])/gu, "$1")
    .replaceAll(/([.,])\s*([.,])/gu, "$1")
    .trim();

  return spoken;
}

/**
 * Everything the voice says on a step, in the order the card says it.
 *
 * Two departures from "read what is on screen", and both are deliberate.
 *
 * **The step's name is not spoken.** The copy carries it as a heading —
 * "Désinfecter le site intramusculaire - Désinfectez le quadrant…" — and the
 * card renders it as one, above the instruction, in bold, where it stays put
 * while the voice talks. Read aloud it is the same words twice in a row, and
 * the second time is the one that tells her what to do (Anaïs, 2026-09-22). So
 * the voice reads the instruction, which is the whole of what the step asks;
 * nothing that describes an action is dropped, and `splitStepCopy` is the same
 * function the card uses, so the spoken text is exactly the paragraph she is
 * looking at.
 *
 * **Cautions are spoken whole.** They have no heading to skip — the card
 * renders them as one run of text — and they are the part a patient using the
 * voice *because her hands are busy* would otherwise never hear.
 *
 * Warnings that stop the walk are not here: those take over the screen, and
 * what is said there is said by the surface that shows them.
 */
export function spokenStepText(
  step: ProtocolStep,
  context: ProtocolCopyContext,
): string {
  const instruction = splitStepCopy(
    renderProtocolCopy(step.copy, context),
  ).body;

  return [
    instruction,
    ...(step.warnings ?? [])
      .filter((warning) => warning.severity === "caution")
      .map((warning) => renderProtocolCopy(warning.copy, context)),
  ]
    .map((text) => speakableStepText(text, context.locale))
    .join(" ");
}

/**
 * Choosing which of the device's voices reads the step.
 *
 * Passing only a language leaves the engine to pick, and every platform picks
 * the same way: the smallest voice installed. iOS ships a compact voice and
 * downloads the enhanced one lazily, Android defaults to its local synthesiser,
 * and browsers list the local voices first. The good voice is usually already
 * there and simply not asked for.
 *
 * So this ranks what is available rather than accepting the default. It is
 * scoring, not matching, because the candidates differ wildly between
 * platforms — identifiers, naming, and how quality is signalled are all
 * different — and a rule that insisted on any one of them would fall back to
 * nothing on the other two. Returning `null` is a real answer: it means speak
 * with the engine's own choice, which is what happened before this existed.
 *
 * Nothing chosen here reaches the network — a voice that reports itself remote,
 * by flag or by name, is refused rather than ranked. See `isRemoteVoice`.
 */

/**
 * Words platforms use for their better **on-device** voices.
 *
 * `enhanced`, `premium` and the Siri families are downloadable Apple voices
 * that synthesise locally. They are the whole reason this function exists.
 */
const BETTER_VOICE_MARKERS = ["enhanced", "premium", "siri"];

/**
 * Words that mark a voice synthesised on somebody else's computer.
 *
 * These were in the list above, scoring `+20`, which meant this function was
 * actively **preferring** to upload the step. What it reads out includes the
 * brand she was prescribed and her dose — "the full prescribed amount, 250
 * micrograms" — so a Google or Azure voice sends her medication and her dose to
 * a third party, mid-procedure, from a package whose entire argument is that
 * nothing leaves the device. There was even a test pinning the behaviour.
 *
 * `network` is Android's own suffix for server-side synthesis;
 * `wavenet`/`studio`/`neural`/`journey` are Google Cloud voice families;
 * Microsoft's `natural` voices are Azure. A voice naming any of them is
 * **disqualified**, not penalised — a preference can be outweighed by a good
 * enough region match, and no region match should be able to buy an upload.
 */
const REMOTE_VOICE_MARKERS = [
  "network",
  "wavenet",
  "studio",
  "neural",
  "natural",
  "journey",
  "online",
  "cloud",
];

/**
 * Voices a platform ships that were recorded from a person and named after one.
 *
 * A weaker signal than `quality: "enhanced"`, and it exists because most
 * platforms do not report quality at all. Within a list where nothing is marked,
 * the named system families are the ones built from human recordings; the
 * unnamed rest are the formant synthesisers and the generic fallbacks. Apple's
 * are the Siri and system voices, Microsoft's are the desktop SAPI ones.
 *
 * Scored small on purpose. It breaks a tie between voices the platform will not
 * rank itself, and it must never outweigh an enhanced voice or a language match.
 */
const NATURAL_VOICE_NAMES = [
  // Apple, French
  "amélie",
  "amelie",
  "aurélie",
  "aurelie",
  "thomas",
  "marie",
  // Apple, English
  "samantha",
  "ava",
  "allison",
  "serena",
  "karen",
  "moira",
  "alex",
  "tom",
  // Microsoft desktop
  "hortense",
  "julie",
  "paul",
  "zira",
  "david",
  "hazel",
  "george",
];

/**
 * Android's own suffix for a voice that synthesises on the device.
 *
 * Its voices carry no human name — `fr-fr-x-vlf-local`, `en-us-x-tpd-network` —
 * so the only thing distinguishing the good downloaded ones from the fallback
 * is that suffix. It is the counterpart of `network` in the disqualified list:
 * the same list, the same naming scheme, the opposite answer.
 */
const LOCAL_VOICE_MARKER = "-local";

/**
 * Words that mark the small, robotic end.
 *
 * `compact` is iOS's low-footprint bundle, `espeak` and `eloquence` are the
 * formant synthesisers Android and iOS keep for accessibility. All three are
 * intelligible and none of them sound like a person.
 */
const WORSE_VOICE_MARKERS = ["compact", "espeak", "eloquence", "compacte"];

/** The exact tags worth preferring, in order, per locale. */
const PREFERRED_TAGS: Record<ProtocolLocale, readonly string[]> = {
  fr: ["fr-fr", "fr-ca", "fr-be", "fr-ch"],
  en: ["en-us", "en-gb", "en-au", "en-ca", "en-ie"],
};

export type SpeechVoice = {
  identifier: string;
  name: string;
  language: string;
  /** "Enhanced" where the platform reports it; anything else is treated as default. */
  quality?: string;
  /**
   * Whether the platform says this voice runs on the device.
   *
   * The web speech API reports it and `expo-speech` passes it through; the
   * native platforms do not, so it is absent rather than false there. An
   * explicit `false` is the strongest signal available that synthesis happens
   * elsewhere, and it disqualifies the voice outright.
   */
  localService?: boolean;
};

/**
 * Whether this voice would send the step somewhere to be spoken.
 *
 * Absent information is treated as local: most platforms report nothing, and
 * disqualifying every silent voice would leave nothing to pick. What is caught
 * is a voice that *says* it is remote, by flag or by name.
 */
export function isRemoteVoice(voice: SpeechVoice): boolean {
  if (voice.localService === false) return true;
  const haystack = `${voice.identifier} ${voice.name}`.toLowerCase();
  return REMOTE_VOICE_MARKERS.some((marker) => haystack.includes(marker));
}

function scoreVoice(voice: SpeechVoice, locale: ProtocolLocale): number {
  const language = voice.language?.toLowerCase().replaceAll("_", "-") ?? "";
  if (!language.startsWith(locale)) return -1;

  // Disqualified, not penalised. A better region match must never be able to
  // outweigh an upload of her dose.
  if (isRemoteVoice(voice)) return -1;

  let score = 0;

  // Region matters more than anything else here: a French step read by a
  // Canadian voice is still right, read by an English voice it is not French at
  // all. Earlier in the list scores higher.
  const preferredIndex = PREFERRED_TAGS[locale].indexOf(language);
  score += preferredIndex >= 0 ? 40 - preferredIndex * 4 : 10;

  if (voice.quality?.toLowerCase() === "enhanced") score += 30;

  const haystack = `${voice.identifier} ${voice.name}`.toLowerCase();
  if (BETTER_VOICE_MARKERS.some((marker) => haystack.includes(marker))) {
    score += 20;
  }
  if (WORSE_VOICE_MARKERS.some((marker) => haystack.includes(marker))) {
    score -= 35;
  }
  if (NATURAL_VOICE_NAMES.some((name) => haystack.includes(name))) {
    score += 8;
  }
  // Said by the platform rather than guessed from a name: Android marks its
  // on-device voices in the identifier, and the web engine reports it as a
  // flag. Either one means this is a voice the device actually installed, not
  // the engine's last resort.
  if (haystack.includes(LOCAL_VOICE_MARKER) || voice.localService === true) {
    score += 6;
  }

  return score;
}

/**
 * The best installed voice for her language, or `null` to let the engine choose.
 *
 * Deterministic: ties break on identifier, so the same device does not pick a
 * different voice between two steps of the same walk. A voice changing halfway
 * through a procedure reads as a second speaker.
 */
export function pickVoice(
  voices: readonly SpeechVoice[],
  locale: ProtocolLocale,
): SpeechVoice | null {
  let best: SpeechVoice | null = null;
  let bestScore = 0;

  for (const voice of voices) {
    if (!voice?.identifier) continue;
    const score = scoreVoice(voice, locale);
    if (score <= 0) continue;
    if (
      score > bestScore ||
      (score === bestScore &&
        best !== null &&
        voice.identifier < best.identifier)
    ) {
      best = voice;
      bestScore = score;
    }
  }

  return best;
}

/** BCP 47 tags the platform speech engines recognise. */
export function speechLanguage(locale: ProtocolLocale): string {
  return locale === "fr" ? "fr-FR" : "en-US";
}

/**
 * A little under conversational pace.
 *
 * She is following the words with her hands, not listening to a paragraph, and
 * the default rate on most engines is tuned for reading prose aloud.
 */
export const SPEECH_RATE = 0.92;
